import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

/**
 * A panel anchored to its trigger — the base of every dropdown that holds
 * more than a list of commands: the searchable pickers, the filter menus and
 * the date range. (A menu of actions is DropdownMenu.)
 *
 * Radix keeps focus inside while open, closes on Escape and outside click,
 * and hands focus back to the trigger on close; it also stacks correctly
 * inside a Dialog or Sheet.
 */
const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // Stays inside the viewport and as tall as there is room for, so a long
      // list near the bottom of the screen scrolls rather than running off it.
      collisionPadding={8}
      className={cn(
        "z-50 max-h-[var(--radix-popover-content-available-height)] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
